import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchEstablishments,
  updateEstablishment,
  toggleEstablishmentStatus,
  activateEstablishment,
  deactivateEstablishment,
  createEstablishment,
} from "./thunks";

import { Establishment } from "./types";
import { toast } from "react-toastify";

interface PaginatedEstablishments {
  data: Establishment[];
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

interface EstablishmentsState {
  establishments: PaginatedEstablishments;
  loading: boolean;
  error: string | null;
}

const initialState: EstablishmentsState = {
  establishments: {
    data: [],
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  },
  loading: false,
  error: null,
};

const establishmentsSlice = createSlice({
  name: "establishments",
  initialState,
  reducers: {
    clearEstablishments: (state) => {
      state.establishments = {
        data: [],
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1,
      };
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEstablishments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchEstablishments.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          // Estrutura atual da API (Laravel pagination):
          // { status, message, data: { data: T[], meta: { current_page, per_page, total, last_page }, links } }
          // Suporta também formatos antigos: { data: { data, current_page, per_page, total, last_page } }
          if (payload?.data && payload.data.data) {
            const pg = payload.data.meta || payload.data; // meta preferencial, senão campos planos
            state.establishments = {
              data: payload.data.data,
              current_page: Number(pg.current_page) || 1,
              per_page: Number(pg.per_page) || 15,
              total:
                Number(pg.total) ||
                (Array.isArray(payload.data.data)
                  ? payload.data.data.length
                  : 0),
              last_page: Number(pg.last_page) || 1,
            };
          } else {
            // Fallback se estrutura for diferente
            const arr = Array.isArray(payload?.data) ? payload.data : [];
            state.establishments = {
              data: arr,
              current_page: 1,
              per_page: 15,
              total: arr.length || 0,
              last_page: 1,
            };
          }
        },
      )
      .addCase(fetchEstablishments.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as { error: string })?.error || "Erro desconhecido";
      })
      // Update establishment cases
      .addCase(updateEstablishment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEstablishment.fulfilled, (state, action) => {
        state.loading = false;
        const updatedEstablishment = action.payload.data;

        // Atualizar o estabelecimento na lista
        const index = state.establishments.data.findIndex(
          (est) => est.id === updatedEstablishment.id,
        );

        if (index !== -1) {
          state.establishments.data[index] = updatedEstablishment;
        }
      })
      .addCase(updateEstablishment.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as { error: string })?.error ||
          "Erro ao atualizar estabelecimento";
        toast.error(
          (action.payload as { error: string })?.error ||
            "Erro ao atualizar estabelecimento",
        );
      })
      // Create establishment cases
      .addCase(createEstablishment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEstablishment.fulfilled, (state, action) => {
        state.loading = false;
        const created = action.payload?.data;
        if (created) {
          // Inserir no topo e ajustar total
          state.establishments.data = [created, ...state.establishments.data];
          state.establishments.total += 1;
        }
      })
      .addCase(createEstablishment.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as { error: string })?.error ||
          "Erro ao criar estabelecimento";
        toast.error(
          (action.payload as { error: string })?.error ||
            "Erro ao criar estabelecimento",
        );
      })
      // Toggle status cases
      .addCase(toggleEstablishmentStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleEstablishmentStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { id, status } = action.meta.arg;

        // Atualizar o status do estabelecimento na lista
        const index = state.establishments.data.findIndex(
          (est) => est.id === id,
        );
        if (index !== -1) {
          state.establishments.data[index].status = status;
          state.establishments.data[index].is_active = status === "active";
        }
      })
      .addCase(toggleEstablishmentStatus.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as { error: string })?.error ||
          "Erro ao alterar status";
      })
      // Activate establishment cases
      .addCase(activateEstablishment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateEstablishment.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload;

        // Atualizar o status do estabelecimento na lista
        const index = state.establishments.data.findIndex(
          (est) => est.id === id,
        );
        if (index !== -1) {
          state.establishments.data[index].status = "active";
          state.establishments.data[index].is_active = true;
        }
      })
      .addCase(activateEstablishment.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as { error: string })?.error ||
          "Erro ao ativar estabelecimento";
      })
      // Deactivate establishment cases
      .addCase(deactivateEstablishment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deactivateEstablishment.fulfilled, (state, action) => {
        state.loading = false;
        const { id } = action.payload;

        // Atualizar o status do estabelecimento na lista
        const index = state.establishments.data.findIndex(
          (est) => est.id === id,
        );
        if (index !== -1) {
          state.establishments.data[index].status = "inactive";
          state.establishments.data[index].is_active = false;
        }
      })
      .addCase(deactivateEstablishment.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as { error: string })?.error ||
          "Erro ao desativar estabelecimento";
      });
  },
});

export const { clearEstablishments } = establishmentsSlice.actions;
export default establishmentsSlice.reducer;
